import { create } from "zustand";
import { persist } from "zustand/middleware";
import { FEATURE_FLAG_DEFAULTS, type FeatureFlagKey } from "@/featureFlags";
import { updateUserFeatureFlag } from "@/helpers/firestore_helpers";

interface FeatureFlagStore {
  /** Current flag values (merged from all sources). */
  flags: Record<FeatureFlagKey, boolean>;

  /** Whether Firestore flags have been synced at least once this session. */
  hasSyncedFromFirestore: boolean;

  /**
   * Toggle a flag locally and persist to Firestore if a userId is provided.
   * This is the primary action for user-facing toggles.
   */
  setFlag: (
    flag: FeatureFlagKey,
    enabled: boolean,
    userId?: string | null,
  ) => void;

  /**
   * Sync flags from Firestore user document into the store.
   * Called after login or when userInDb updates.
   * Firestore values take precedence over stale local values.
   */
  syncFromFirestore: (
    firestoreFlags: Partial<Record<FeatureFlagKey, boolean>> | undefined,
  ) => void;
}

export const useFeatureFlagStore = create<FeatureFlagStore>()(
  persist(
    (set) => ({
      flags: { ...FEATURE_FLAG_DEFAULTS },
      hasSyncedFromFirestore: false,

      setFlag: async (flag, enabled, userId) => {
        set((state) => ({
          flags: { ...state.flags, [flag]: enabled },
        }));

        if (userId) {
          try {
            await updateUserFeatureFlag(userId, flag, enabled);
          } catch (error) {
            console.error("Failed to sync feature flag to Firestore:", error);
          }
        }
      },

      syncFromFirestore: (firestoreFlags) => {
        if (!firestoreFlags) {
          set({ hasSyncedFromFirestore: true });
          return;
        }

        set((state) => ({
          flags: { ...state.flags, ...firestoreFlags },
          hasSyncedFromFirestore: true,
        }));
      },
    }),
    {
      name: "featureFlags",
      version: 1,
      partialize: (state) => ({
        flags: state.flags,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<FeatureFlagStore>;
        return {
          ...currentState,
          flags: {
            ...FEATURE_FLAG_DEFAULTS,
            ...(persisted.flags ?? {}),
          },
          // Reset sync flag on page load — Firestore will re-sync
          hasSyncedFromFirestore: false,
        };
      },
    },
  ),
);
